import { memo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FolderKanban, Plus, AlertTriangle, Clock, CheckCircle2,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatCard } from '@/components/shared/StatCard';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { WorkflowProject } from '@/types/api';

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-teal/10 text-teal-light',
  blocked: 'bg-compliance-critical/10 text-compliance-critical',
  completed: 'bg-compliance-low/10 text-compliance-low',
  cancelled: 'bg-muted text-muted-foreground',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'text-compliance-critical',
  high: 'text-compliance-high',
  medium: 'text-compliance-medium',
  low: 'text-compliance-low',
};

function SkeletonCard() {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
          <div className="flex gap-2">
            <div className="h-5 w-16 animate-pulse rounded bg-muted" />
            <div className="h-5 w-20 animate-pulse rounded bg-muted" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-3 w-12 animate-pulse rounded bg-muted" />
          <div className="h-1.5 w-20 animate-pulse rounded bg-muted" />
        </div>
      </div>
    </div>
  );
}

export function ComplianceProjectsPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? 'all';
  const visibleCount = parseInt(searchParams.get('show') ?? '20', 10);

  const setStatusFilter = useCallback((v: string) => {
    setSearchParams((prev) => {
      prev.set('status', v);
      prev.delete('show');
      return prev;
    });
  }, [setSearchParams]);

  const loadMore = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('show', String(visibleCount + 20));
      return prev;
    });
  }, [setSearchParams, visibleCount]);

  const { data: summaryData } = useQuery({
    queryKey: ['workflow-project-summary'],
    queryFn: () => api.projects.summary(),
    staleTime: 30_000,
    enabled: !!user,
  });

  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['workflow-projects', statusFilter],
    queryFn: () => api.projects.list(
      statusFilter !== 'all' ? { status: statusFilter } : undefined,
    ),
    staleTime: 30_000,
    enabled: !!user,
  });

  const projects = projectsData?.projects ?? [];
  const summary = summaryData;

  const handleProjectClick = useCallback((projectId: string) => {
    navigate(`/projects/${projectId}`);
  }, [navigate]);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Compliance Projects"
        subtitle="Track regulatory compliance initiatives across departments"
        actions={
          <Button
            className="bg-teal text-white hover:bg-teal/90"
            onClick={() => navigate('/projects/new')}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
        }
      />

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <StatCard label="Active Projects" value={summary.open + summary.inProgress} icon={FolderKanban} colorOverride="text-teal" />
          <StatCard label="Overdue" value={summary.overdue} icon={AlertTriangle} colorOverride={summary.overdue > 0 ? 'text-compliance-critical' : undefined} />
          <StatCard label="Avg Progress" value={`${Math.round(summary.avgProgressPct)}%`} icon={Clock} />
          <StatCard label="Completed" value={summary.completed} icon={CheckCircle2} colorOverride="text-compliance-low" />
        </div>
      )}

      {/* Filter tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="in_progress">In Progress</TabsTrigger>
          <TabsTrigger value="blocked">Blocked</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Project list */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects found"
          description={statusFilter !== 'all'
            ? `No ${statusFilter.replace('_', ' ')} projects. Try a different filter.`
            : 'Create your first compliance project to start tracking regulatory initiatives.'}
        />
      ) : (
        <div className="space-y-3">
          {projects.slice(0, visibleCount).map((project) => (
            <ProjectCard
              key={project.projectId}
              project={project}
              onClick={handleProjectClick}
            />
          ))}
          {visibleCount < projects.length && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Showing {Math.min(visibleCount, projects.length)} of {projects.length} projects
              </p>
              <Button variant="outline" size="sm" onClick={loadMore}>
                Load More
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ProjectCard = memo(function ProjectCard({
  project,
  onClick,
}: {
  readonly project: WorkflowProject;
  readonly onClick: (id: string) => void;
}) {
  const isOverdue = project.deadline && new Date(project.deadline) < new Date() && project.status !== 'completed';

  return (
    <button
      type="button"
      className="w-full text-left"
      onClick={() => onClick(project.projectId)}
      aria-label={`View project: ${project.title}`}
    >
      <Card className="cursor-pointer transition-colors hover:bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-sm font-semibold text-foreground">
                  {project.title}
                </h3>
                <Badge variant="outline" className={cn('shrink-0 text-[10px]', STATUS_COLORS[project.status])}>
                  {project.status.replace('_', ' ')}
                </Badge>
                {project.createdByType === 'agent' && (
                  <Badge variant="outline" className="shrink-0 text-[10px] bg-purple-100 text-purple-800">AI Generated</Badge>
                )}
                {project.riskScore != null && (
                  <Badge variant="outline" className={cn(
                    'shrink-0 text-[10px]',
                    project.riskScore > 70 ? 'bg-compliance-critical/10 text-compliance-critical'
                      : project.riskScore > 30 ? 'bg-compliance-medium/10 text-compliance-medium'
                        : 'bg-compliance-low/10 text-compliance-low',
                  )}>
                    Risk: {project.riskScore}
                  </Badge>
                )}
              </div>
              {project.description && (
                <p className="mt-1 truncate text-xs text-muted-foreground">{project.description}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {project.departments.map((dept) => (
                  <span key={dept} className="rounded bg-muted px-1.5 py-0.5">{dept}</span>
                ))}
                {project.frameworks.map((fw) => (
                  <span key={fw} className="rounded bg-teal/10 px-1.5 py-0.5 text-teal-light">{fw}</span>
                ))}
                {project.deadline && (
                  <span className={cn('flex items-center gap-1', isOverdue && 'text-compliance-critical font-medium')}>
                    <Clock className="h-3 w-3" />
                    {new Date(project.deadline).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className={cn('text-xs font-semibold', PRIORITY_COLORS[project.priority])}>
                {project.priority.toUpperCase()}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {project.tasksCompleted}/{project.taskCount}
                </span>
                <Progress value={project.progressPct} className="h-1.5 w-20" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </button>
  );
});
